<template>
  <template v-if="hasAnyParam">
    <div class="dkn-admin-statuses mt-2">
      <div
        v-for="status in quickStatuses"
        :key="status.key"
        class="dkn-admin-status"
        :class="status.toneClass"
      >
        <div class="dkn-admin-status-header">
          <i :class="status.icon" />
          <span>{{ status.label }}</span>
        </div>
        <div class="dkn-admin-status-value">
          {{ status.value }}
        </div>
      </div>
    </div>

    <!-- Grupa 2: Diagnostyka -->
    <div
      v-if="d10DiagnosticsItems.length"
      class="dkn-mps-section"
    >
      <div class="dkn-mps-section-title">
        <i class="bi bi-exclamation-triangle" />
        <span>{{ $t('components.assetDetails.dknUartGroupTitles.diagnostics') }}</span>
      </div>
      <div class="asset-stats-grid">
        <div
          v-for="item in d10DiagnosticsItems"
          :key="item.key"
          class="dkn-admin-status dkn-param-card"
          :class="item.toneClass"
        >
          <div class="dkn-admin-status-header">
            <i class="bi bi-clipboard-data" />
            <span>{{ item.label }}</span>
          </div>
          <div class="dkn-admin-status-value">
            {{ item.value }}
          </div>
        </div>
      </div>
    </div>

    <!-- Grupa 3: Temperatury i ciśnienia -->
    <div
      v-if="tempPressureItems.length"
      class="dkn-mps-section"
    >
      <div class="dkn-mps-section-title">
        <i class="bi bi-thermometer-sun" />
        <span>{{ $t('components.assetDetails.dknUartGroupTitles.tempsAndPressures') }}</span>
      </div>
      <div class="asset-stats-grid">
        <div
          v-for="item in tempPressureItems"
          :key="item.key"
          class="dkn-admin-status dkn-param-card"
          :class="item.toneClass"
        >
          <div class="dkn-admin-status-header">
            <i class="bi bi-thermometer-half" />
            <span>{{ item.label }}</span>
          </div>
          <div class="dkn-admin-status-value">
            {{ item.value }}
          </div>
        </div>
      </div>
    </div>

    <!-- Grupa 4: Sprężarka, wentylatory, zawory -->
    <div
      v-if="compressorFansValveItems.length"
      class="dkn-mps-section"
    >
      <div class="dkn-mps-section-title">
        <i class="bi bi-gear-wide-connected" />
        <span>{{ $t('components.assetDetails.dknUartGroupTitles.compressorFansValves') }}</span>
      </div>
      <div class="asset-stats-grid">
        <div
          v-for="item in compressorFansValveItems"
          :key="item.key"
          class="dkn-admin-status dkn-param-card"
          :class="item.toneClass"
        >
          <div class="dkn-admin-status-header">
            <i class="bi bi-gear" />
            <span>{{ item.label }}</span>
          </div>
          <div class="dkn-admin-status-value">
            {{ item.value }}
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="d10FlagsItems.length"
      class="dkn-mps-section"
    >
      <div class="dkn-mps-section-title">
        <i class="bi bi-toggle-on" />
        <span>{{ $t('components.assetDetails.dknUartGroupTitles.d10Flags') }}</span>
      </div>
      <div class="asset-stats-grid">
        <div
          v-for="item in d10FlagsItems"
          :key="item.key"
          class="dkn-admin-status dkn-param-card"
          :class="item.toneClass"
        >
          <div class="dkn-admin-status-header">
            <i class="bi bi-toggles" />
            <span>{{ item.label }}</span>
          </div>
          <div class="dkn-admin-status-value">
            {{ item.value }}
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="d10ProtectionItems.length"
      class="dkn-mps-section"
    >
      <div class="dkn-mps-section-title">
        <i class="bi bi-shield-check" />
        <span>{{ $t('components.assetDetails.dknUartGroupTitles.d10Protection') }}</span>
      </div>
      <div class="asset-stats-grid">
        <div
          v-for="item in d10ProtectionItems"
          :key="item.key"
          class="dkn-admin-status dkn-param-card"
          :class="item.toneClass"
        >
          <div class="dkn-admin-status-header">
            <i class="bi bi-shield" />
            <span>{{ item.label }}</span>
          </div>
          <div class="dkn-admin-status-value">
            {{ item.value }}
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="d00ConfigItems.length"
      class="dkn-mps-section"
    >
      <div class="dkn-mps-section-title">
        <i class="bi bi-info-circle" />
        <span>{{ $t('components.assetDetails.dknUartGroupTitles.d00Config') }}</span>
      </div>
      <div class="asset-stats-grid">
        <div
          v-for="item in d00ConfigItems"
          :key="item.key"
          class="dkn-admin-status dkn-param-card"
          :class="item.toneClass"
        >
          <div class="dkn-admin-status-header">
            <i class="bi bi-hash" />
            <span>{{ item.label }}</span>
          </div>
          <div class="dkn-admin-status-value">
            {{ item.value }}
          </div>
        </div>
      </div>
    </div>
  </template>

  <div
    v-else
    class="modbus-empty"
  >
    <i class="bi bi-wind" />
    <span>{{ $t('components.assetDetails.dknUartMessages.noData') }}</span>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

const UART_PREFIX = 'DKN_UART_'
type Tone = 'neutral' | 'ok' | 'warn' | 'bad'

const OUTDOOR_TEMP_KEYS = new Set([
  'DKN_UART_D20_OUTDOOR_AIR_TEMP_C105_O0_B2',
  'DKN_UART_D20_TI_C105_O2_B2',
  'DKN_UART_D20_TL_C105_O4_B2',
  'DKN_UART_D20_TG_C105_O6_B2',
  'DKN_UART_D20_TD1_C105_O8_B2',
  'DKN_UART_D21_INV_FIN_TEMP_C105_O8_B2',
])

const PRESSURE_KEYS = new Set([
  'DKN_UART_D20_LOW_PRESSURE_1_C105_O14_B2',
  'DKN_UART_D20_LOW_PRESSURE_1_T_C405_O14_B2',
  'DKN_UART_D21_HIGH_PRESSURE_C105_O0_B2',
  'DKN_UART_D21_HIGH_PRESSURE_T_C405_O0_B2',
  'DKN_UART_D21_INV_SECONDARY_CURRENT_A_C105_O2_B2',
])

const COMPRESSOR_KEYS = new Set([
  'DKN_UART_D30_COMPRESSOR_ROTATION_FREQUENCY_RPS_C152_O0_B1',
  'DKN_UART_D30_FAN_1_STEP_C211_O2_B1',
  'DKN_UART_D30_FAN_2_STEP_C211_O3_B1',
  'DKN_UART_D30_R1_C305_O12_B1',
  'DKN_UART_D30_WARNING_C304_O12_B1',
  'DKN_UART_D30_BACK_UP_C303_O12_B1',
])

const EV_KEYS = new Set([
  'DKN_UART_D30_EXPANSION_VALVE_2_PLS_C151_O6_B2',
  'DKN_UART_D30_EXPANSION_VALVE_3_PLS_C151_O8_B2',
])

// Grupa 2 – Diagnostyka
const D10_DIAGNOSTICS_KEYS = new Set([
  'DKN_UART_D10_ERROR_TYPE_C203_O4_B1',
  'DKN_UART_D10_TARGET_EVAP_PRESS_C116_O6_B2',
])

// Grupa 5 – Stany pracy (thermostatOnOff przeniesiony do quickStatuses; defrost przeniesiony tu z quickStatuses)
const D10_FLAGS_KEYS = new Set([
  'DKN_UART_D10_DEFROST_OPERATION_C304_O1_B1',
  'DKN_UART_D10_RESTART_STANDBY_C306_O1_B1',
  'DKN_UART_D10_OIL_RETURN_OPERATION_C303_O1_B1',
  'DKN_UART_D10_PRESSURE_EQUALIZING_OPERATION_C302_O1_B1',
  'DKN_UART_D10_DEMAND_SIGNAL_C301_O1_B1',
  'DKN_UART_D10_MODE_COMPLETION_C305_O1_B1',
  'DKN_UART_D10_LOW_NOISE_CONTROL_C300_O1_B1',
])

// Grupa 6 – Zabezpieczenia (swVer przeniesiony tu z quickStatuses)
const D10_PROTECTION_KEYS = new Set([
  'DKN_UART_D10_DISCHARGE_TEMP_DROP_C307_O10_B1',
  'DKN_UART_D10_DISCHARGE_TEMP_PROTECTION_RETRY_QTY_C310_O10_B1',
  'DKN_UART_D10_COMP_INV_CURRENT_DROP_C303_O10_B1',
  'DKN_UART_D10_COMP_INV_CURRENT_PROTECTION_RETRY_QTY_C311_O10_B1',
  'DKN_UART_D10_HP_DROP_CONTROL_C307_O11_B1',
  'DKN_UART_D10_HP_PROTECTION_RETRY_QTY_C310_O11_B1',
  'DKN_UART_D10_LP_DROP_CONTROL_C303_O11_B1',
  'DKN_UART_D10_LP_PROTECTION_RETRY_QTY_C311_O11_B1',
  'DKN_UART_D10_FIN_TEMP_DROP_CONTROL_C307_O12_B1',
  'DKN_UART_D10_FIN_TEMP_PROTECTION_RETRY_QTY_C310_O12_B1',
  'DKN_UART_D10_OTHER_DROP_CONTROL_C303_O12_B1',
  'DKN_UART_D10_SOFTWARE_VERSION_C151_O13_B2',
])

const D00_CONFIG_KEYS = new Set([
  'DKN_UART_D00_SENSOR_DATA_QTY_C152_O0_B1',
  'DKN_UART_D00_INV_COMPRESSOR_QTY_C152_O1_B1',
  'DKN_UART_D00_STD_COMPRESSOR_QTY_C152_O2_B1',
  'DKN_UART_D00_FAN_DATA_QTY_C152_O3_B1',
  'DKN_UART_D00_EXPANSION_VALVE_DATA_QTY_C152_O4_B1',
  'DKN_UART_D00_4_WAY_VALVE_DATA_QTY_C152_O5_B1',
  'DKN_UART_D00_CRANK_CASE_HEATER_QTY_C152_O6_B1',
  'DKN_UART_D00_SOLENOID_VALVE_QTY_C152_O7_B1',
  'DKN_UART_D00_MAX_CONNECTABLE_INDOOR_UNITS_C152_O8_B1',
  'DKN_UART_D00_CONNECTED_INDOOR_UNIT_QTY_C152_O9_B1',
  'DKN_UART_D00_MAX_CONNECTABLE_BOOSTER_UNITS_C152_O10_B1',
  'DKN_UART_D00_CONNECTED_BOOSTER_UNIT_QTY_C152_O11_B1',
])

const OPERATION_MODE_KEYS: Record<number, string> = {
  0: 'components.assetDetails.dknUartStates.operationModes.stop',
  1: 'components.assetDetails.dknUartStates.operationModes.cooling',
  2: 'components.assetDetails.dknUartStates.operationModes.heating',
  3: 'components.assetDetails.dknUartStates.operationModes.fanOnly',
  4: 'components.assetDetails.dknUartStates.operationModes.dry',
  5: 'components.assetDetails.dknUartStates.operationModes.auto',
}

const PARAM_LABEL_KEYS: Record<string, string> = {
  DKN_UART_D10_OPERATION_MODE_C152_O0_B1:                    'components.assetDetails.dknUartLabels.operationMode',
  DKN_UART_D10_ERROR_CODE_C204_O5_B1:                        'components.assetDetails.dknUartLabels.errorCode',
  DKN_UART_D10_DEFROST_OPERATION_C304_O1_B1:                 'components.assetDetails.dknUartLabels.defrost',
  DKN_UART_D10_SOFTWARE_VERSION_C151_O13_B2:                 'components.assetDetails.dknUartLabels.softwareVersion',
  DKN_UART_D20_OUTDOOR_AIR_TEMP_C105_O0_B2:                  'components.assetDetails.dknUartLabels.outdoorAirTemp',
  DKN_UART_D20_TI_C105_O2_B2:                                'components.assetDetails.dknUartLabels.ti',
  DKN_UART_D20_TL_C105_O4_B2:                                'components.assetDetails.dknUartLabels.tl',
  DKN_UART_D20_TG_C105_O6_B2:                                'components.assetDetails.dknUartLabels.tg',
  DKN_UART_D20_TD1_C105_O8_B2:                               'components.assetDetails.dknUartLabels.td1',
  DKN_UART_D20_TD2_C105_O10_B2:                              'components.assetDetails.dknUartLabels.td2',
  DKN_UART_D20_TD3_C105_O12_B2:                              'components.assetDetails.dknUartLabels.td3',
  DKN_UART_D20_LOW_PRESSURE_1_C105_O14_B2:                   'components.assetDetails.dknUartLabels.lowPressure1',
  DKN_UART_D20_LOW_PRESSURE_1_T_C405_O14_B2:                 'components.assetDetails.dknUartLabels.lowPressure1T',
  DKN_UART_D21_HIGH_PRESSURE_C105_O0_B2:                     'components.assetDetails.dknUartLabels.highPressure',
  DKN_UART_D21_HIGH_PRESSURE_T_C405_O0_B2:                   'components.assetDetails.dknUartLabels.highPressureT',
  DKN_UART_D21_INV_SECONDARY_CURRENT_A_C105_O2_B2:           'components.assetDetails.dknUartLabels.invCurrent',
  DKN_UART_D21_INV_FIN_TEMP_C105_O8_B2:                      'components.assetDetails.dknUartLabels.invFinTemp',
  DKN_UART_D30_COMPRESSOR_ROTATION_FREQUENCY_RPS_C152_O0_B1: 'components.assetDetails.dknUartLabels.compressorFreq',
  DKN_UART_D30_FAN_1_STEP_C211_O2_B1:                        'components.assetDetails.dknUartLabels.fan1',
  DKN_UART_D30_FAN_2_STEP_C211_O3_B1:                        'components.assetDetails.dknUartLabels.fan2',
  DKN_UART_D30_R1_C305_O12_B1:                               'components.assetDetails.dknUartLabels.r1',
  DKN_UART_D30_WARNING_C304_O12_B1:                          'components.assetDetails.dknUartLabels.warning',
  DKN_UART_D30_BACK_UP_C303_O12_B1:                          'components.assetDetails.dknUartLabels.backUp',
  DKN_UART_D30_EXPANSION_VALVE_2_PLS_C151_O6_B2:             'components.assetDetails.dknUartLabels.ev2',
  DKN_UART_D30_EXPANSION_VALVE_3_PLS_C151_O8_B2:             'components.assetDetails.dknUartLabels.ev3',
  DKN_UART_D10_THERMOSTAT_ON_OFF_C307_O1_B1:                         'components.assetDetails.dknUartLabels.thermostatOnOff',
  DKN_UART_D10_RESTART_STANDBY_C306_O1_B1:                           'components.assetDetails.dknUartLabels.restartStandby',
  DKN_UART_D10_OIL_RETURN_OPERATION_C303_O1_B1:                      'components.assetDetails.dknUartLabels.oilReturnOperation',
  DKN_UART_D10_PRESSURE_EQUALIZING_OPERATION_C302_O1_B1:             'components.assetDetails.dknUartLabels.pressureEqualizingOperation',
  DKN_UART_D10_DEMAND_SIGNAL_C301_O1_B1:                             'components.assetDetails.dknUartLabels.demandSignal',
  DKN_UART_D10_MODE_COMPLETION_C305_O1_B1:                           'components.assetDetails.dknUartLabels.modeCompletion',
  DKN_UART_D10_LOW_NOISE_CONTROL_C300_O1_B1:                         'components.assetDetails.dknUartLabels.lowNoiseControl',
  DKN_UART_D10_ERROR_TYPE_C203_O4_B1:                                'components.assetDetails.dknUartLabels.errorType',
  DKN_UART_D10_TARGET_EVAP_PRESS_C116_O6_B2:                         'components.assetDetails.dknUartLabels.targetEvapPress',
  DKN_UART_D10_DISCHARGE_TEMP_DROP_C307_O10_B1:                      'components.assetDetails.dknUartLabels.dischargeTempDrop',
  DKN_UART_D10_DISCHARGE_TEMP_PROTECTION_RETRY_QTY_C310_O10_B1:      'components.assetDetails.dknUartLabels.dischargeTempProtRetries',
  DKN_UART_D10_COMP_INV_CURRENT_DROP_C303_O10_B1:                    'components.assetDetails.dknUartLabels.compInvCurrentDrop',
  DKN_UART_D10_COMP_INV_CURRENT_PROTECTION_RETRY_QTY_C311_O10_B1:   'components.assetDetails.dknUartLabels.compInvCurrentProtRetries',
  DKN_UART_D10_HP_DROP_CONTROL_C307_O11_B1:                          'components.assetDetails.dknUartLabels.hpDropControl',
  DKN_UART_D10_HP_PROTECTION_RETRY_QTY_C310_O11_B1:                  'components.assetDetails.dknUartLabels.hpProtRetries',
  DKN_UART_D10_LP_DROP_CONTROL_C303_O11_B1:                          'components.assetDetails.dknUartLabels.lpDropControl',
  DKN_UART_D10_LP_PROTECTION_RETRY_QTY_C311_O11_B1:                  'components.assetDetails.dknUartLabels.lpProtRetries',
  DKN_UART_D10_FIN_TEMP_DROP_CONTROL_C307_O12_B1:                    'components.assetDetails.dknUartLabels.finTempDropControl',
  DKN_UART_D10_FIN_TEMP_PROTECTION_RETRY_QTY_C310_O12_B1:            'components.assetDetails.dknUartLabels.finTempProtRetries',
  DKN_UART_D10_OTHER_DROP_CONTROL_C303_O12_B1:                       'components.assetDetails.dknUartLabels.otherDropControl',
  DKN_UART_D00_SENSOR_DATA_QTY_C152_O0_B1:                           'components.assetDetails.dknUartLabels.d00SensorQty',
  DKN_UART_D00_INV_COMPRESSOR_QTY_C152_O1_B1:                        'components.assetDetails.dknUartLabels.d00InvCompressorQty',
  DKN_UART_D00_STD_COMPRESSOR_QTY_C152_O2_B1:                        'components.assetDetails.dknUartLabels.d00StdCompressorQty',
  DKN_UART_D00_FAN_DATA_QTY_C152_O3_B1:                              'components.assetDetails.dknUartLabels.d00FanQty',
  DKN_UART_D00_EXPANSION_VALVE_DATA_QTY_C152_O4_B1:                  'components.assetDetails.dknUartLabels.d00EvQty',
  DKN_UART_D00_4_WAY_VALVE_DATA_QTY_C152_O5_B1:                      'components.assetDetails.dknUartLabels.d004WayValveQty',
  DKN_UART_D00_CRANK_CASE_HEATER_QTY_C152_O6_B1:                     'components.assetDetails.dknUartLabels.d00CrankCaseHeaterQty',
  DKN_UART_D00_SOLENOID_VALVE_QTY_C152_O7_B1:                        'components.assetDetails.dknUartLabels.d00SolenoidValveQty',
  DKN_UART_D00_MAX_CONNECTABLE_INDOOR_UNITS_C152_O8_B1:               'components.assetDetails.dknUartLabels.d00MaxIndoorUnits',
  DKN_UART_D00_CONNECTED_INDOOR_UNIT_QTY_C152_O9_B1:                  'components.assetDetails.dknUartLabels.d00ConnectedIndoorUnits',
  DKN_UART_D00_MAX_CONNECTABLE_BOOSTER_UNITS_C152_O10_B1:             'components.assetDetails.dknUartLabels.d00MaxBoosterUnits',
  DKN_UART_D00_CONNECTED_BOOSTER_UNIT_QTY_C152_O11_B1:                'components.assetDetails.dknUartLabels.d00ConnectedBoosterUnits',
}

function toNumber(raw: any): number | null {
  const normalized = normalizeRawValue(raw)
  if (normalized === null) return null
  const parsed = parseFloat(normalized.replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : null
}

function normalizeRawValue(raw: any): string | null {
  if (raw === null || raw === undefined) return null

  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? String(raw) : null
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    return trimmed === '' ? null : trimmed
  }

  if (typeof raw === 'object') {
    if ('value' in raw) return normalizeRawValue(raw.value)
    if ('raw' in raw) return normalizeRawValue(raw.raw)
  }

  return null
}

function formatRawFallback(raw: any, unit: string = ''): string {
  const normalized = normalizeRawValue(raw)
  if (normalized === null) return '—'
  return unit ? `${normalized} ${unit}` : normalized
}

function formatTemp(raw: any): string {
  const n = toNumber(raw)
  return n === null ? formatRawFallback(raw, '°C') : `${n.toFixed(1)} °C`
}

function formatPressure(raw: any): string {
  const n = toNumber(raw)
  return n === null ? formatRawFallback(raw, 'bar') : `${n.toFixed(1)} bar`
}

function formatPls(raw: any): string {
  const n = toNumber(raw)
  return n === null ? formatRawFallback(raw, 'pls') : `${n} pls`
}

function formatVal(raw: any): string {
  const n = toNumber(raw)
  if (n === null) return formatRawFallback(raw)
  return Number.isInteger(n) ? String(n) : n.toFixed(2)
}

function toneClass(tone: Tone): string {
  return `dkn-tone-${tone}`
}

function getParamTone(name: string, raw: any): Tone {
  const value = toNumber(raw)
  if (value === null) return 'neutral'
  if (name === 'DKN_UART_D10_ERROR_CODE_C204_O5_B1') {
    return value > 0 ? 'bad' : 'ok'
  }
  if (name === 'DKN_UART_D10_DEFROST_OPERATION_C304_O1_B1') return value > 0 ? 'warn' : 'ok'
  if (name === 'DKN_UART_D10_OPERATION_MODE_C152_O0_B1') return value > 0 ? 'ok' : 'neutral'
  if (name === 'DKN_UART_D30_R1_C305_O12_B1') return value > 0 ? 'ok' : 'neutral'
  if (name === 'DKN_UART_D30_WARNING_C304_O12_B1') return value > 0 ? 'warn' : 'ok'
  if (name === 'DKN_UART_D30_BACK_UP_C303_O12_B1') return value > 0 ? 'warn' : 'ok'
  if (name === 'DKN_UART_D10_THERMOSTAT_ON_OFF_C307_O1_B1') return value > 0 ? 'ok' : 'neutral'
  if (name === 'DKN_UART_D10_DEMAND_SIGNAL_C301_O1_B1') return value > 0 ? 'ok' : 'neutral'
  if (name === 'DKN_UART_D10_MODE_COMPLETION_C305_O1_B1') return value > 0 ? 'ok' : 'neutral'
  if (
    name === 'DKN_UART_D10_RESTART_STANDBY_C306_O1_B1' ||
    name === 'DKN_UART_D10_OIL_RETURN_OPERATION_C303_O1_B1' ||
    name === 'DKN_UART_D10_PRESSURE_EQUALIZING_OPERATION_C302_O1_B1'
  ) return value > 0 ? 'warn' : 'ok'
  if (name === 'DKN_UART_D10_ERROR_TYPE_C203_O4_B1') return value > 0 ? 'bad' : 'ok'
  if (name.startsWith('DKN_UART_D10_') && name.includes('_DROP_')) return value > 0 ? 'warn' : 'ok'
  return 'neutral'
}

interface ParamItem {
  key: string
  label: string
  value: string
  toneClass: string
}

export default defineComponent({
  name: 'DknUartLatestParamsPanel',
  props: {
    latestParams: {
      type: Array,
      required: true
    }
  },
  computed: {
    uartByName(): Record<string, any> {
      const params = Array.isArray(this.latestParams) ? this.latestParams : []
      const seen = new Set<string>()
      return params
        .filter((p: any) => {
          const name = p?.can_params_io?.param_name
          if (typeof name !== 'string' || !name.startsWith(UART_PREFIX) || seen.has(name)) return false
          seen.add(name)
          return true
        })
        .reduce((acc: Record<string, any>, p: any) => {
          acc[p.can_params_io.param_name] = p
          return acc
        }, {})
    },
    hasAnyParam(): boolean {
      return Object.keys(this.uartByName).length > 0
    },
    // Grupa 1 – Status pracy
    quickStatuses(): Array<{ key: string; label: string; value: string; icon: string; toneClass: string }> {
      const g = (key: string) => this.uartByName[key]?.value

      const opMode    = toNumber(g('DKN_UART_D10_OPERATION_MODE_C152_O0_B1'))
      const thermostat = toNumber(g('DKN_UART_D10_THERMOSTAT_ON_OFF_C307_O1_B1'))
      const errCode   = toNumber(g('DKN_UART_D10_ERROR_CODE_C204_O5_B1'))
      const errType   = toNumber(g('DKN_UART_D10_ERROR_TYPE_C203_O4_B1'))

      return [
        {
          key: 'opMode',
          label: this.tString('components.assetDetails.dknUartLabels.operationMode'),
          icon: 'bi bi-fan',
          value: opMode === null ? '—' : this.getOperationModeLabel(opMode),
          toneClass: toneClass(opMode !== null && opMode > 0 ? 'ok' : 'neutral')
        },
        {
          key: 'thermostat',
          label: this.tString('components.assetDetails.dknUartLabels.thermostatOnOff'),
          icon: 'bi bi-thermometer',
          value: thermostat === null ? '—' : thermostat > 0
            ? this.tString('components.assetDetails.dknUartStates.on')
            : this.tString('components.assetDetails.dknUartStates.off'),
          toneClass: toneClass(thermostat !== null && thermostat > 0 ? 'ok' : 'neutral')
        },
        {
          key: 'error',
          label: this.tString('components.assetDetails.dknUartLabels.error'),
          icon: 'bi bi-exclamation-circle',
          value: errCode === null
            ? '—'
            : errCode > 0
              ? `${this.tString('components.assetDetails.dknUartLabels.errorType')}: ${errType ?? '—'}\n${this.tString('components.assetDetails.dknUartLabels.errorCode')}: ${errCode}`
              : this.tString('components.assetDetails.dknUartStates.none'),
          toneClass: toneClass(errCode !== null && errCode > 0 ? 'bad' : 'ok')
        },
      ]
    },
    // Grupa 2 – Diagnostyka
    d10DiagnosticsItems(): ParamItem[] {
      return this.buildItems(D10_DIAGNOSTICS_KEYS, (name, raw) => {
        if (name.includes('C116_')) return formatPressure(raw)
        return formatVal(raw)
      })
    },
    outdoorTempItems(): ParamItem[] {
      const items = this.buildItems(OUTDOOR_TEMP_KEYS, (_name, raw) => formatTemp(raw))

      const ti   = toNumber(this.uartByName['DKN_UART_D20_TI_C105_O2_B2']?.value)
      const lp1t = toNumber(this.uartByName['DKN_UART_D20_LOW_PRESSURE_1_T_C405_O14_B2']?.value)
      if (ti !== null && lp1t !== null) {
        items.push({
          key: 'suctionSh',
          label: this.tString('components.assetDetails.dknUartLabels.suctionSh'),
          value: `${(ti - lp1t).toFixed(1)} K`,
          toneClass: toneClass('neutral'),
        })
      }

      const td1 = toNumber(this.uartByName['DKN_UART_D20_TD1_C105_O8_B2']?.value)
      const hpt = toNumber(this.uartByName['DKN_UART_D21_HIGH_PRESSURE_T_C405_O0_B2']?.value)
      if (td1 !== null && hpt !== null) {
        items.push({
          key: 'dischargeSh',
          label: this.tString('components.assetDetails.dknUartLabels.dischargeSh'),
          value: `${(td1 - hpt).toFixed(1)} K`,
          toneClass: toneClass('neutral'),
        })
      }

      return items
    },
    pressureItems(): ParamItem[] {
      return this.buildItems(PRESSURE_KEYS, (name, raw) => {
        if (name.includes('_T_C405_') || name === 'DKN_UART_D21_INV_SECONDARY_CURRENT_A_C105_O2_B2') {
          return name.includes('_A_C105_') ? `${toNumber(raw)?.toFixed(1) ?? '—'} A` : formatTemp(raw)
        }
        return formatPressure(raw)
      })
    },
    // Grupa 3 – scalona
    tempPressureItems(): ParamItem[] {
      return [...this.outdoorTempItems, ...this.pressureItems]
    },
    compressorItems(): ParamItem[] {
      return this.buildItems(COMPRESSOR_KEYS, (name, raw) => {
        if (name.includes('_FAN_')) {
          const n = toNumber(raw)
          return n === null ? '—' : String(n)
        }
        if (name.includes('_R1_') || name.includes('_WARNING_') || name.includes('_BACK_UP_')) {
          const n = toNumber(raw)
          return n === null ? '—' : n > 0
            ? this.tString('components.assetDetails.dknUartStates.on')
            : this.tString('components.assetDetails.dknUartStates.off')
        }
        return `${toNumber(raw) ?? '—'} rps`
      })
    },
    evItems(): ParamItem[] {
      return this.buildItems(EV_KEYS, (_name, raw) => formatPls(raw))
    },
    // Grupa 4 – scalona
    compressorFansValveItems(): ParamItem[] {
      return [...this.compressorItems, ...this.evItems]
    },
    d10FlagsItems(): ParamItem[] {
      return this.buildItems(D10_FLAGS_KEYS, (_name, raw) => {
        const n = toNumber(raw)
        return n === null ? '—' : n > 0
          ? this.tString('components.assetDetails.dknUartStates.on')
          : this.tString('components.assetDetails.dknUartStates.off')
      })
    },
    d10ProtectionItems(): ParamItem[] {
      return this.buildItems(D10_PROTECTION_KEYS, (name, raw) => {
        if (name.includes('_QTY_')) return formatVal(raw)
        if (name.includes('_DROP_')) {
          const n = toNumber(raw)
          return n === null ? '—' : n > 0
            ? this.tString('components.assetDetails.dknUartStates.on')
            : this.tString('components.assetDetails.dknUartStates.off')
        }
        return formatVal(raw)
      })
    },
    d00ConfigItems(): ParamItem[] {
      return this.buildItems(D00_CONFIG_KEYS, (_name, raw) => formatVal(raw))
    },
  },
  methods: {
    tString(key: string): string {
      return this.$t(key) as string
    },
    getParamLabel(name: string): string {
      const key = PARAM_LABEL_KEYS[name]
      return key ? this.tString(key) : name
    },
    getOperationModeLabel(value: number): string {
      const key = OPERATION_MODE_KEYS[value]
      return key ? this.tString(key) : String(value)
    },
    buildItems(
      keySet: Set<string>,
      formatter: (name: string, raw: any) => string
    ): ParamItem[] {
      return [...keySet]
        .filter((name) => name in this.uartByName)
        .map((name) => {
          const p = this.uartByName[name]
          const raw = p?.value
          return {
            key: `${name}-${p?.id ?? ''}`,
            label: this.getParamLabel(name),
            value: formatter(name, raw),
            toneClass: toneClass(getParamTone(name, raw))
          }
        })
    }
  }
})
</script>

<style scoped lang="scss">
.dkn-admin-statuses {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
  margin-bottom: 10px;
}

.dkn-admin-status,
.dkn-param-card {
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-left: 4px solid rgba(148, 163, 184, 0.65);
  border-radius: 12px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.55);
}

.dkn-admin-statuses .dkn-admin-status-value {
  white-space: pre-line;
}

.dkn-admin-status-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 6px;
}

.dkn-admin-status-value {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.25;
}

.asset-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
}

.dkn-mps-section {
  margin-top: 14px;
}

.dkn-mps-section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
}

.modbus-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px;
  color: #94a3b8;
  font-size: 14px;

  i {
    font-size: 28px;
  }
}

.dkn-tone-ok {
  border-left-color: rgba(22, 163, 74, 0.9) !important;

  .dkn-admin-status-value {
    color: #166534;
  }
}

.dkn-tone-warn {
  border-left-color: rgba(234, 179, 8, 0.95) !important;

  .dkn-admin-status-value {
    color: #92400e;
  }
}

.dkn-tone-bad {
  border-left-color: rgba(220, 38, 38, 0.95) !important;

  .dkn-admin-status-value {
    color: #991b1b;
  }
}
</style>
